import { Table } from "@tanstack/react-table";
import { useEffect } from "react";

interface UseTableScrollAndResizeProps<TData> {
  /**
   * Reference to the table container div element that will be observed for scroll and resize events
   */
  tableContainerRef: React.ForwardedRef<HTMLDivElement>;
  /**
   * Callback fired when the table container is scrolled
   */
  onScroll?: (scrollLeft: number, scrollTop: number) => void;
  /**
   * Callback fired when the table container is resized. This is a handler that will be called only on window resize.
   */
  onResize?: (rect: DOMRectReadOnly) => void;
  /**
   * TanStack table instance
   */
  table: Table<TData>;
}

/**
 * Hook that handles scroll and resize events for a table container
 * Sets up ResizeObserver and scroll event listeners to track container dimensions and scroll position
 */
export function useTableScrollAndResize<TData>({
  tableContainerRef,
  onScroll,
  onResize,
  table,
}: UseTableScrollAndResizeProps<TData>) {
  useEffect(() => {
    if (!tableContainerRef) {
      return;
    }

    const element =
      typeof tableContainerRef === "function"
        ? null
        : tableContainerRef.current;

    if (!element) {
      return;
    }

    // Create ResizeObserver if onResize callback is provided
    const resizeObserver = onResize
      ? new ResizeObserver((entries) => {
          for (const entry of entries) {
            onResize(entry.contentRect);
          }
        })
      : null;

    if (resizeObserver) {
      resizeObserver.observe(element);
    }

    // Scroll handler that reports scroll position
    const handleScroll = () => {
      if (element) {
        onScroll?.(element.scrollLeft, element.scrollTop);
      }
    };

    if (onScroll) {
      element.addEventListener("scroll", handleScroll);
    }

    // Cleanup function to remove observers and event listeners
    return () => {
      resizeObserver?.disconnect();
      if (onScroll) {
        element.removeEventListener("scroll", handleScroll);
      }
    };
  }, [table, onResize, onScroll, tableContainerRef]);
}
